/* ============================================================================
   LA ENTRADA — la pantalla

   POR QUÉ ES UNA PANTALLA APARTE Y NO UN MODAL

   Un modal sobre la aplicación deja ver lo que hay debajo, y lo que hay debajo
   son las horas de vuelo de una empresa. Una entrada que se puede esquivar
   cerrando un cuadro no es una entrada.

   ═══ DOS COLUMNAS: LA OPERACIÓN Y EL FORMULARIO ═══

   A la izquierda, la operación: cuatro fotos de helicóptero que se van
   turnando, con una frase cada una. A la derecha, el formulario. La columna de
   la foto es **decorativa y lo dice** (`aria-hidden`): quien usa un lector de
   pantalla no tiene por qué escuchar cuatro frases de folleto antes de llegar
   al campo del correo.

   En una pantalla angosta la foto pasa arriba y el formulario abajo. Y si el
   teléfono está acostado —poca altura— la foto desaparece: en 360 px de alto lo
   único que importa es el formulario.

   ═══ SIGUE SIENDO OSCURA, Y ES A PROPÓSITO ═══

   La entrada que sirvió de modelo es clara. Esta no puede serlo: el iPad va
   montado en la cabina y un formulario blanco a pleno sol no se lee. La
   estructura se copia —foto, marca, campos con ícono, pie— y el color no.

   ═══ LO QUE DICE Y LO QUE NO ═══

   · El motivo del rechazo se muestra **tal cual lo escribió el servidor**. El
     servidor usa el mismo texto para el correo que no existe, la contraseña
     equivocada y la empresa dada de baja, a propósito: distinguirlos convierte
     este formulario en un buscador de correos válidos.
   · Dice **a qué entra cada perfil**, y lo dice leyendo la matriz de permisos
     (`seccionesDe`), no una lista escrita a mano: el día que un permiso cambie,
     esta pantalla cambia sola. Un piloto que llega acá no sabe que su compañero
     de administración ve otra aplicación.
   · Y lleva el aviso legal al pie, como toda la aplicación: es una herramienta
     de apoyo, no un documento certificado.
   ========================================================================== */
import { useEffect, useRef, useState } from "react";

import { PERFILES } from "../domain/identidad.js";
import { VISTAS, seccionesDe, vistaDePerfil } from "../domain/entrada.js";
import { ingresar as ingresarDeVerdad } from "../services/sesion/sesion.js";
import "./pantallaDeIngreso.css";

/* ═══ LAS CUATRO ESCENAS DE LA COLUMNA IZQUIERDA ═══

   Las fotos vienen de Unsplash; los originales quedaron en `fotos-origen/`, que
   no se publica. Se comprimieron a 1600 px y calidad 78: los ocho originales
   sumaban 13,5 MB y estas cuatro, 732 KB. Un fondo de entrada no puede pesar más
   que la aplicación que abre detrás, y esto se abre en un helipuerto.

   La frase de cada una no adorna: dice para qué sirve la herramienta, que es lo
   primero que pregunta quien llega por primera vez. */
const ESCENAS = [
  {
    foto: "/img/entrada/entrada-01.jpg",
    titulo: "Cada minuto cuenta",
    pie: "Peso, balance y performance calculados antes de despegar.",
  },
  {
    foto: "/img/entrada/entrada-02.jpg",
    titulo: "Planificar, no improvisar",
    pie: "Distancia, combustible y reservas, tramo por tramo.",
  },
  {
    foto: "/img/entrada/entrada-03.jpg",
    titulo: "El terreno manda",
    pie: "Altitud densidad, obstáculos, viento, cartas y METAR.",
  },
  {
    foto: "/img/entrada/entrada-04.jpg",
    titulo: "Y también dice cuándo no volar",
    pie: "El riesgo se mide en tierra.",
  },
];

/* ═══ LOS ÍCONOS DE LOS PERFILES ═══

   Son de la pantalla, no del dominio: `identidad.js` no tiene por qué saber que
   esto se dibuja. Si mañana se agrega un perfil a la matriz, la lista lo va a
   mostrar igual, con el ícono genérico —que es lo que corresponde: un perfil
   nuevo se ve, en vez de desaparecer de la pantalla por no tener dibujo. */
const TRAZOS_DE_PERFIL = {
  pilot: "M2 5h16M10 5v3M5 11h9l2 3H4zM5 17h9",
  company_admin: "M4 21V6l8-3 8 3v15M9 21v-4h6v4M9 10h.01M15 10h.01M9 14h.01M15 14h.01",
  super_admin: "M12 3a9 9 0 100 18 9 9 0 000-18M3 12h18M12 3c3 3.5 3 14 0 18M12 3c-3 3.5-3 14 0 18",
  auditor: "M11 5a6 6 0 100 12 6 6 0 000-12M20 20l-4.4-4.4M8.6 11.4l2 2 3.4-3.4",
  maintenance: "M20.5 5.5a5 5 0 01-6.8 6.8L6 20l-2-2 7.7-7.7a5 5 0 016.8-6.8l-3 3 2 2 3-3z",
};
const TRAZO_GENERICO = "M12 4.6a3.4 3.4 0 100 6.8 3.4 3.4 0 000-6.8M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6";

function IconoDePerfil({ clave }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={TRAZOS_DE_PERFIL[clave] || TRAZO_GENERICO} />
    </svg>
  );
}

/**
 * A qué entra un perfil, leído de la matriz.
 *
 * No se escribe a mano: se le pregunta al dominio, que es el mismo que usa el
 * servidor para dejar pasar. Así esta pantalla no puede ofrecer una puerta que
 * el servidor vaya a cerrar con un 403 delante del usuario.
 */
function destinoDe(perfil) {
  if (vistaDePerfil(perfil) === VISTAS.VUELO) {
    return "Vista de vuelo: planificar, calcular y registrar";
  }
  const secciones = seccionesDe(perfil).map((s) => s.nombre);
  if (!secciones.length) return "Gestión, sin secciones asignadas";
  return "Gestión: " + secciones.join(", ");
}

/* ═══ EL ORDEN DE LA LISTA ES DE LA PANTALLA, NO DEL DOMINIO ═══

   Primero el piloto —el que más entra y el que menos sabe qué hay del otro
   lado—, después el administrador de su empresa, y al final los perfiles de
   consulta. `PERFILES` está en el orden de la matriz, que es otro orden: ahí
   primero va el de plataforma, y a quien entra a volar eso no le dice nada.

   Un perfil que no esté en esta lista se muestra igual, al final. Agregar un
   perfil a la matriz no puede hacerlo desaparecer de la pantalla: el `puesto`
   le da un lugar en vez de dejarlo afuera por no estar nombrado acá. */
const ORDEN_EN_LA_ENTRADA = ["pilot", "company_admin", "super_admin", "auditor", "maintenance"];

const puesto = (clave) => {
  const i = ORDEN_EN_LA_ENTRADA.indexOf(clave);
  return i === -1 ? ORDEN_EN_LA_ENTRADA.length : i;
};

const EN_ORDEN_DE_ENTRADA = [...PERFILES].sort((a, b) => puesto(a.clave) - puesto(b.clave));

/* ═══ EL RELOJ ═══

   Con `Intl.DateTimeFormat` armado una sola vez: hacerlo adentro del
   temporizador construye un formateador por segundo, y eso cuesta más que
   formatear.

   La hora Zulu no es un adorno: los METAR, los TAF y los NOTAM vienen en UTC, y
   el piloto que lee un informe antes de entrar tiene que poder compararlo sin
   hacer la cuenta. */
const FORMATO_FECHA = new Intl.DateTimeFormat("es", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const FORMATO_HORA = new Intl.DateTimeFormat("es", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});
const FORMATO_ZULU = new Intl.DateTimeFormat("es", {
  timeZone: "UTC",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const relojDe = (cuando) => ({
  local: `${FORMATO_FECHA.format(cuando)} · ${FORMATO_HORA.format(cuando)}`,
  zulu: `${FORMATO_ZULU.format(cuando)} Z`,
});

/* El año de ESTA copia. Se lee una vez, cuando el módulo se carga, y no en cada
   dibujo: leer el reloj adentro del `render` es una función que devuelve cosas
   distintas con los mismos argumentos. */
const ANO_DE_ESTA_COPIA = new Date().getFullYear();

export default function PantallaDeIngreso({
  alEntrar,
  ingresar = ingresarDeVerdad,
  sello = null,
  aviso = null,
}) {
  const [email, setEmail] = useState("");
  const [clave, setClave] = useState("");
  const [verClave, setVerClave] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const campoEmail = useRef(null);
  const relojLocal = useRef(null);
  const relojZulu = useRef(null);

  /* ═══ POR QUÉ EL RELOJ NO ES UN ESTADO ═══

     Con `useState` y un `setInterval`, cada segundo React vuelve a dibujar la
     pantalla ENTERA —las seis marcas, los cinco renglones de perfiles, los
     íconos— mientras alguien está escribiendo su correo. Y en una prueba, ese
     `setState` cae fuera del `act` y ensucia la salida de toda la suite.

     Los dos `<time>` se dibujan VACÍOS y el temporizador escribe su texto: con
     el nodo sin hijos, React no tiene texto que volver a poner, así que no hay
     nada que se pelee por el contenido. Es un reloj, no un dato de la
     aplicación. */
  useEffect(() => {
    const pintar = () => {
      const reloj = relojDe(new Date());
      if (relojLocal.current) relojLocal.current.textContent = reloj.local;
      if (relojZulu.current) relojZulu.current.textContent = reloj.zulu;
    };

    pintar();
    const tic = setInterval(pintar, 1000);
    return () => clearInterval(tic);
  }, []);

  const enviar = async (evento) => {
    evento.preventDefault();
    if (enviando) return;

    setError(null);
    setEnviando(true);
    const r = await ingresar(email.trim(), clave);
    setEnviando(false);

    /* La contraseña se borra SIEMPRE, también cuando entra bien: un campo con
       la clave escrita que queda en pantalla es una clave en pantalla. */
    setClave("");

    if (r.ok) {
      alEntrar?.(r.usuario);
      return;
    }
    setError(r.motivo || "No se pudo entrar.");
    campoEmail.current?.focus?.();
  };

  return (
    <div className="ing">
      {/* La operación. Decorativa: el nombre y el lema están en el panel del
          formulario, así que quien no la ve no se pierde nada. */}
      <aside className="ing__muestra" aria-hidden="true">
        {ESCENAS.map((escena, i) => (
          <img
            key={escena.foto}
            className="ing__foto"
            src={escena.foto}
            alt=""
            width="1600"
            height="1067"
            decoding="async"
            style={{ animationDelay: `${i * 7}s` }}
          />
        ))}
        <span className="ing__velo" />

        <div className="ing__muestra-marca">
          <img src="/iconos/helicalc-icono.svg" alt="" width="46" height="46" />
          <span>HeliCalc</span>
        </div>

        <div className="ing__escenas">
          {ESCENAS.map((escena, i) => (
            <p key={escena.titulo} className="ing__escena" style={{ animationDelay: `${i * 7}s` }}>
              <strong>{escena.titulo}</strong>
              <span>{escena.pie}</span>
            </p>
          ))}
        </div>
      </aside>

      <main className="ing__panel">
        <div className="ing__caja">
          <header className="ing__marca">
            <img className="ing__logo" src="/iconos/helicalc-icono.svg" alt="" width="60" height="60" />
            <span className="ing__marca-texto">
              <span className="ing__marca-nombre">HeliCalc</span>
              <span className="ing__marca-lema">Planificación de vuelo de helicóptero</span>
            </span>
          </header>

          <h1 className="ing__titulo">Iniciar sesión</h1>
          <p className="ing__intro">
            Ingresa con tu correo y tu contraseña. La aplicación que se abre depende
            de tu perfil.
          </p>

          <form className="ing__form" onSubmit={enviar} aria-label="Entrar">
            <label className="ing__campo">
              <span className="ing__etiqueta">Correo</span>
              <span className="ing__control">
                <svg
                  className="ing__icono"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M12 4.6a3.4 3.4 0 100 6.8 3.4 3.4 0 000-6.8M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" />
                </svg>
                <input
                  ref={campoEmail}
                  type="email"
                  name="email"
                  autoComplete="username"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="piloto@empresa.com"
                  aria-label="Correo"
                />
              </span>
            </label>

            <label className="ing__campo">
              <span className="ing__etiqueta">Contraseña</span>
              <span className="ing__control ing__control--clave">
                <svg
                  className="ing__icono"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M7 10V7.5a5 5 0 0110 0V10M5.5 10h13v10h-13zM12 14v2.5" />
                </svg>
                <input
                  type={verClave ? "text" : "password"}
                  name="clave"
                  autoComplete="current-password"
                  required
                  value={clave}
                  onChange={(e) => setClave(e.target.value)}
                  placeholder="Tu contraseña"
                  aria-label="Contraseña"
                />
                <button
                  type="button"
                  className="ing__ver"
                  onClick={() => setVerClave((v) => !v)}
                  aria-pressed={verClave}
                  aria-label={verClave ? "Ocultar" : "Ver"}
                  title={verClave ? "Ocultar la contraseña" : "Mostrar la contraseña"}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="19"
                    height="19"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    {verClave ? (
                      <>
                        <path d="M3 3l18 18" />
                        <path d="M10.6 5.2A9.7 9.7 0 0112 5c5.2 0 9 5 9 7a11 11 0 01-2.6 3.5M6.3 7.3C3.9 8.8 3 10.6 3 12c0 2 3.8 7 9 7a9.9 9.9 0 004-.8" />
                        <path d="M9.9 9.9a3 3 0 004.2 4.2" />
                      </>
                    ) : (
                      <>
                        <path d="M3 12c0-2 3.8-7 9-7s9 5 9 7-3.8 7-9 7-9-5-9-7z" />
                        <circle cx="12" cy="12" r="3" />
                      </>
                    )}
                  </svg>
                </button>
              </span>
            </label>

            {/* El rechazo vive pegado al formulario y se anuncia: quien usa un
                lector de pantalla se entera sin salir del campo. */}
            {error && (
              <p className="ing__error" role="alert">
                {error}
              </p>
            )}

            <button type="submit" className="ing__entrar" disabled={enviando}>
              {enviando ? "Entrando…" : "Ingresar"}
              {!enviando && (
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M5 12h13M12.5 6l6 6-6 6" />
                </svg>
              )}
            </button>
          </form>

          {/* La tabla de a quién le toca qué. Sale de `PERFILES` y de la matriz
              de permisos, así que no puede desincronizarse del servidor. */}
          <section className="ing__perfiles" aria-labelledby="ing-perfiles">
            <h2 className="ing__perfiles-titulo" id="ing-perfiles">
              Quién entra y a dónde
            </h2>
            <ul className="ing__perfiles-lista">
              {EN_ORDEN_DE_ENTRADA.map((p) => (
                <li className="ing__perfil" key={p.clave}>
                  <span className="ing__perfil-icono">
                    <IconoDePerfil clave={p.clave} />
                  </span>
                  <span className="ing__perfil-datos">
                    <span className="ing__perfil-nombre">{p.nombre}</span>
                    <span className="ing__perfil-destino">{destinoDe(p.clave)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* No hay «recuperar contraseña»: la contraseña la asigna un
              administrador desde el servidor. Ofrecer un enlace que no lleva a
              nada es peor que no ofrecerlo, y prometer un correo de recuperación
              que nadie manda es peor todavía. */}
          <p className="ing__ayuda">
            ¿No tienes usuario? Lo crea quien administra el servidor con{" "}
            <code>node scripts/crear-usuario.mjs</code>. No hay usuario de fábrica: es
            la primera entrada de la instalación.
          </p>

          {aviso && <p className="ing__aviso-servidor">{aviso}</p>}

          <footer className="ing__pie">
            <p className="ing__legal">
              Herramienta de apoyo a la decisión. No reemplaza el RFM ni a la autoridad
              aeronáutica.
            </p>
            <p className="ing__meta">
              <span className="ing__reloj">
                <time ref={relojLocal} />
                <time className="ing__zulu" ref={relojZulu} title="Hora Zulu, la de los METAR y los NOTAM" />
              </span>
              <span>
                {sello && <span className="ing__sello">{sello} · </span>}
                © {ANO_DE_ESTA_COPIA} QSoftware.biz
              </span>
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
}
